/**
 * Scene Config Behavior
 * 
 * Configures global scene settings from a marker mesh. Parameters:
 * - gravity: Physics gravity (default -30)
 * - ambient: Uses mesh material's albedo color as ambient light color
 * - camera_type: Camera mode
 * - cull_distance: Spatial culling distance
 * - hdr_url: HDR environment texture URL (optional)
 * - env_intensity: Environment lighting intensity
 * - env_rotation: Environment rotation in radians
 * - env_blur: Skybox blur amount
 * 
 * This is a configuration marker - no runtime state needed.
 */

import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { EntityType } from '../../types/EntityTypes';
import type { EntityBehavior, NodeProcessContext, NodeProcessResult } from '../../core/BehaviorRegistry';
import type { BehaviorParameterSchema } from '../../core/BehaviorSchema';
import { logger } from '../../core/Logger';

const parameters: BehaviorParameterSchema = {
  // Physics & Camera
  gravity: {
    type: 'number',
    default: -30,
    min: -100,
    max: 0,
    description: 'Physics gravity (negative = downward)'
  },
  // Note: ambient color is read from mesh material's albedo color, not from parameters
  camera_type: {
    type: 'enum',
    enumValues: ['follow-player', 'fixed', 'cinematic', 'orbital'],
    default: 'follow-player',
    description: 'Camera behavior mode'
  },
  cull_distance: {
    type: 'number',
    default: 30,
    min: 10,
    max: 200,
    description: 'Spatial culling distance in units'
  },
  max_lights: {
    type: 'number',
    default: 8,
    min: 1,
    max: 16,
    description: 'Maximum simultaneous lights (affects shader compilation)'
  },
  
  // Environment / Skybox (merged from config:environment)
  hdr_url: {
    type: 'string',
    default: '',
    description: 'HDR environment texture URL (optional, relative to assets)'
  },
  env_intensity: {
    type: 'number',
    default: 1.0,
    min: 0,
    max: 5,
    description: 'Environment lighting intensity'
  },
  env_rotation: {
    type: 'number',
    default: 0,
    min: 0,
    max: 6.28,
    description: 'Environment rotation in radians'
  },
  env_blur: {
    type: 'number',
    default: 0.3,
    min: 0,
    max: 1,
    description: 'Skybox blur amount (0=sharp, 1=blurred)'
  },
  
  // Camera / Startup
  start_camera: {
    type: 'string',
    default: '',
    description: 'Name of camera to use for initial view. If camera has animation, plays as intro then uses final position.'
  },
  
  // Fog settings
  fog_enabled: {
    type: 'boolean',
    default: false,
    description: 'Enable distance fog'
  },
  fog_mode: {
    type: 'enum',
    enumValues: ['linear', 'exp', 'exp2'],
    default: 'exp2',
    description: 'Fog falloff mode: linear, exponential, or exponential squared'
  },
  fog_density: {
    type: 'number',
    default: 0.01,
    min: 0.001,
    max: 1.0,
    description: 'Fog density (for exp/exp2 modes)'
  },
  fog_start: {
    type: 'number',
    default: 10,
    min: 0,
    max: 500,
    description: 'Fog start distance (for linear mode)'
  },
  fog_end: {
    type: 'number',
    default: 100,
    min: 0,
    max: 1000,
    description: 'Fog end distance (for linear mode)'
  },
  fog_color: {
    type: 'color',
    default: [0.5, 0.5, 0.6],
    description: 'Fog color (RGB)'
  },
  
  // Lens Effect settings
  lens_enabled: {
    type: 'boolean',
    default: false,
    description: 'Enable lens rendering pipeline (DoF, aberration, grain, etc.)'
  },
  lens_edge_blur: {
    type: 'number',
    default: 0.0,
    min: 0,
    max: 2.0,
    description: 'Edge blur amount (0=none, 1=realistic)'
  },
  lens_chromatic_aberration: {
    type: 'number',
    default: 0.0,
    min: 0,
    max: 2.0,
    description: 'Chromatic aberration amount (0=none, 1=realistic)'
  },
  lens_distortion: {
    type: 'number',
    default: 0.0,
    min: 0,
    max: 2.0,
    description: 'Lens distortion amount (0=none, 1=realistic)'
  },
  lens_grain: {
    type: 'number',
    default: 0.0,
    min: 0,
    max: 1.0,
    description: 'Film grain amount (0=none, 1=heavy)'
  },
  lens_dof_distance: {
    type: 'number',
    default: 50,
    min: 1,
    max: 500,
    description: 'Depth of field focus distance'
  },
  lens_dof_enabled: {
    type: 'boolean',
    default: false,
    description: 'Enable depth of field effect'
  },
  lens_dof_aperture: {
    type: 'number',
    default: 0.03,
    min: 0.01,
    max: 1.0,
    description: 'DoF blur amount (higher=more blur, 0.03=subtle, 0.1+=strong)'
  },
  lens_dof_darken: {
    type: 'number',
    default: 0.0,
    min: 0,
    max: 1.0,
    description: 'Darken out-of-focus areas'
  },
  lens_dof_pentagon: {
    type: 'boolean',
    default: true,
    description: 'Pentagon-shaped bokeh effect'
  },
  lens_dof_gain: {
    type: 'number',
    default: 0.0,
    min: 0,
    max: 2.0,
    description: 'DoF highlights gain (0=disabled DoF)'
  },
  lens_dof_threshold: {
    type: 'number',
    default: 1.0,
    min: 0,
    max: 2.0,
    description: 'DoF highlights threshold'
  }
};

export const sceneConfigBehavior: EntityBehavior = {
  id: 'config:scene',
  displayName: 'Scene Config',
  description: 'Global scene configuration: gravity, ambient light, camera, culling, and environment.',
  type: EntityType.STATIC_GEOMETRY, // Configuration marker
  category: 'config',
  tags: ['config', 'scene', 'settings', 'environment', 'skybox'],
  parameters,
  
  // Process during level load: extract scene configuration
  onProcessNode: (ctx: NodeProcessContext): NodeProcessResult => {
    const gravity = ctx.getParam<number>('gravity', -30);
    const cullDistance = ctx.getParam<number>('cull_distance', 30);
    const maxLights = ctx.getParam<number>('max_lights', 8);
    
    // Read ambient color from mesh material's albedo color
    let ambientR = 0.2, ambientG = 0.2, ambientB = 0.2;
    const mesh = ctx.mesh as Mesh;
    const material = mesh.material;
    
    if (material) {
      if (material instanceof PBRMaterial && material.albedoColor) {
        ambientR = material.albedoColor.r;
        ambientG = material.albedoColor.g;
        ambientB = material.albedoColor.b;
        logger.level.log(`🎨 Scene config: Using PBR albedo color (${ambientR.toFixed(2)}, ${ambientG.toFixed(2)}, ${ambientB.toFixed(2)})`);
      } else if (material instanceof StandardMaterial && material.diffuseColor) {
        ambientR = material.diffuseColor.r;
        ambientG = material.diffuseColor.g;
        ambientB = material.diffuseColor.b;
        logger.level.log(`🎨 Scene config: Using Standard diffuse color (${ambientR.toFixed(2)}, ${ambientG.toFixed(2)}, ${ambientB.toFixed(2)})`);
      } else {
        logger.level.warn(`⚠️ Scene config: Material "${material.name}" has no albedo/diffuse color, using default ambient`);
      }
    } else {
      logger.level.warn(`⚠️ Scene config: Mesh "${mesh.name}" has no material, using default ambient`);
    }
    
    // Store physics/camera config in results
    const startCamera = ctx.getParam<string>('start_camera', '');
    
    // Read fog settings
    const fogEnabled = ctx.getParam<boolean>('fog_enabled', false);
    const fogMode = ctx.getParam<string>('fog_mode', 'exp2');
    const fogDensity = ctx.getParam<number>('fog_density', 0.01);
    const fogStart = ctx.getParam<number>('fog_start', 10);
    const fogEnd = ctx.getParam<number>('fog_end', 100);
    
    // Read fog color - check for color property (stored as _r, _g, _b in Blender)
    // Uses schema default [0.5, 0.5, 0.6] - a neutral gray-blue fog color
    const fogColorR = ctx.getParam<number>('fog_color_r', 0.5);
    const fogColorG = ctx.getParam<number>('fog_color_g', 0.5);
    const fogColorB = ctx.getParam<number>('fog_color_b', 0.6);
    
    // Read lens effect settings
    const lensEnabled = ctx.getParam<boolean>('lens_enabled', false);
    const lensConfig = lensEnabled ? {
      enabled: true,
      edge_blur: ctx.getParam<number>('lens_edge_blur', 0.0),
      chromatic_aberration: ctx.getParam<number>('lens_chromatic_aberration', 0.0),
      distortion: ctx.getParam<number>('lens_distortion', 0.0),
      grain_amount: ctx.getParam<number>('lens_grain', 0.0),
      dof_focus_distance: ctx.getParam<number>('lens_dof_distance', 50),
      dof_enabled: ctx.getParam<boolean>('lens_dof_enabled', false),
      dof_aperture: ctx.getParam<number>('lens_dof_aperture', 0.03),
      dof_darken: ctx.getParam<number>('lens_dof_darken', 0.0),
      dof_pentagon: ctx.getParam<boolean>('lens_dof_pentagon', true),
      dof_gain: ctx.getParam<number>('lens_dof_gain', 1.0),
      dof_threshold: ctx.getParam<number>('lens_dof_threshold', 1.0),
    } : undefined;
    
    ctx.results.mapConfig = {
      physics: { gravity },
      ambient: new Vector3(ambientR, ambientG, ambientB),
      maxLights,
      ...(startCamera ? { startCamera } : {}),
      ...(fogEnabled ? { 
        fog: {
          enabled: true,
          mode: fogMode as 'linear' | 'exp' | 'exp2',
          density: fogDensity,
          start: fogStart,
          end: fogEnd,
          // Fog color from fog_color param or falls back to ambient
          color: new Vector3(fogColorR, fogColorG, fogColorB)
        }
      } : {}),
      ...(lensConfig ? { lens: lensConfig } : {})
    };
    ctx.results.cullDistance = cullDistance;
    
    if (startCamera) {
      logger.level.log(`📷 Scene config: start_camera="${startCamera}"`);
    }
    
    logger.level.log(`🌍 Scene config applied: gravity=${gravity}, ambient=(${ambientR.toFixed(2)}, ${ambientG.toFixed(2)}, ${ambientB.toFixed(2)}), cullDistance=${cullDistance}, maxLights=${maxLights}${fogEnabled ? `, fog=${fogMode}(density=${fogDensity})` : ''}${lensEnabled ? ', lens=enabled' : ''}`);

    // Process environment if HDR URL is provided
    const hdrUrl = ctx.getParam<string>('hdr_url', '');
    if (hdrUrl && ctx.services.environment) {
      // Build environment extras from behavior params
      const envIntensity = ctx.getParam<number>('env_intensity', 1.0);
      const envExtras = {
        hm_behavior_hdr_url: hdrUrl,
        hm_behavior_intensity: envIntensity,
        hm_behavior_sky_brightness: ctx.getParam<number>('sky_brightness', envIntensity * 0.5),
        hm_behavior_rotation_y: ctx.getParam<number>('env_rotation', 0),
        hm_behavior_blur_level: ctx.getParam<number>('env_blur', 0.3),
      };
      ctx.services.environment.processEnvironmentEntity(ctx.mesh as Mesh, envExtras);
    }
    
    // Hide the config marker
    ctx.mesh.isVisible = false;
    
    return { 
      handled: true, 
      skipPhysics: true, 
      hideNode: true 
    };
  },
  
  createMachine: undefined,
  onSpawn: undefined,
};
